import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useGlobalCore } from '../../../../../core/modules/global';
import { UPDATE_PRODUCT_ATTRIBUTE_DIALOG } from '../../../../../core/constants/dialog';
import Spinner from '../../../Spinner';
import { tAttribute, useMasterCore } from '../../../../../core/modules/master';
import { useCompanyCore } from '../../../../../core/modules/company';
import Swal from 'sweetalert2';
import { AttributeLabel } from './CreateDialog';
import { ERROR } from '../../../../../core/constants/errorCode';

interface Props {
  type: tAttribute;
}

const schema = yup.object().shape({
  name: yup.string().required('Campo requerido'),
  has_description: yup.boolean().when('$type', (type) => 
    type[0] === 'state' ? yup.boolean().optional() : yup.boolean().required('Campo requerido')
  ),
});

const UpdateDialog = (props: Props) => {
  const { type } = props;
  const { dialogs, closeDialog } = useGlobalCore();
  const { errorCode, productAttribute: selectedAttribute, putProductAttibute, deleteProductAttibute, getProductAttributes, setProductAttribute } = useMasterCore();
  const { companyId } = useCompanyCore();

  const [loading, setLoading]  = useState(false);

  const { control, formState, handleSubmit, reset, setValue } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    context: { type }, // Pasar el tipo como contexto para el schema
  });
  const { isValid, errors } = formState;

  const resetForm = useCallback(() => {
    reset({ name: '', has_description: false });
    setProductAttribute(null);
    closeDialog(UPDATE_PRODUCT_ATTRIBUTE_DIALOG);
  }, [closeDialog, reset, setProductAttribute]);

  useEffect(() => {
    if (selectedAttribute && selectedAttribute.id) {
      setValue("name", selectedAttribute.name || '');
      // Solo establecer has_description si NO es estado general
      if (type !== 'state') {
        setValue("has_description", selectedAttribute.has_description || false);
      }
    } else {
      // Limpiar el formulario si no hay atributo seleccionado
      reset({ name: '', has_description: false });
    }
  }, [selectedAttribute, setValue, type, reset]);

  const onSubmit = useCallback(
    (values: any) => {
      if (!companyId) {
        Swal.fire({
          title: 'Error',
          text: 'No se ha seleccionado una empresa',
          icon: 'error',
        });
        return;
      }
      
      if (!selectedAttribute || !selectedAttribute.id) {
        Swal.fire({
          title: 'Error',
          text: 'No se ha seleccionado un atributo para editar',
          icon: 'error',
        });
        return;
      }

      setLoading(true);
      // Si es estado general, no incluir has_description
      const submitData = type === 'state' 
        ? { name: values.name, cardinality: selectedAttribute.cardinality }
        : { ...values, cardinality: selectedAttribute.cardinality };
      
      // Debug: verificar los parámetros antes de enviar
      console.log('🔍 UpdateDialog - onSubmit - companyId:', companyId);
      console.log('🔍 UpdateDialog - onSubmit - selectedAttribute.id:', selectedAttribute.id);
      console.log('🔍 UpdateDialog - onSubmit - submitData:', submitData);
      console.log('🔍 UpdateDialog - onSubmit - type:', type);
      
      putProductAttibute(companyId, selectedAttribute.id, submitData, type, () => {
        resetForm();
        getProductAttributes(companyId, type);
        setLoading(false);
        Swal.fire({
          title: '¡Éxito!',
          text: 'Atributo actualizado correctamente',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
        });
      }, () => {
        setLoading(false);
        Swal.fire({
          title: 'Error',
          text: 'No se pudo actualizar el atributo',
          icon: 'error',
        });
      });
    },
    [companyId, selectedAttribute, type, putProductAttibute, resetForm, getProductAttributes]
  );

  const onDelete = useCallback(() => {
    Swal.fire({
      title: "Confimar acción",
      text: `Eliminar el atributo ${selectedAttribute?.name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Si, eliminar!",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed && companyId && selectedAttribute) {
        setLoading(true);

        deleteProductAttibute(
          companyId,
          selectedAttribute.id,
          type,
          () => {
            setLoading(false);
            getProductAttributes(companyId, type);
            resetForm();
          },
          () => {
            setLoading(false);
          }
        );
      }
    });
  }, [companyId, type, deleteProductAttibute, getProductAttributes, resetForm, selectedAttribute]);

  const isOpen = Array.isArray(dialogs) && dialogs.includes(UPDATE_PRODUCT_ATTRIBUTE_DIALOG);
  const hasValidAttribute = Boolean(selectedAttribute && selectedAttribute.id);

  // Debug: verificar que el atributo se está recibiendo correctamente
  useEffect(() => {
    if (isOpen) {
      console.log('🔍 UpdateDialog - isOpen:', isOpen);
      console.log('🔍 UpdateDialog - selectedAttribute:', selectedAttribute);
      console.log('🔍 UpdateDialog - selectedAttribute.id:', selectedAttribute?.id);
      console.log('🔍 UpdateDialog - type:', type);
    }
  }, [isOpen, selectedAttribute, type]);

  return (
    <Dialog
      classes={{
        paper: 'm-8 sm:m-24 min-w-[350px]',
      }}
      onClose={resetForm}
      open={Boolean(isOpen && hasValidAttribute)}
    >
      <DialogContent>
        <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
          <div className="flex flex-col">
            <div className="mb-16 flex items-center">
              <Typography className="text-2xl font-bold truncate">
                Modificar {AttributeLabel[type]}
              </Typography>
            </div>
      
            <div className="w-full max-w-3xl mt-20">
              <div className="flex flex-col space-y-32">
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                  <div className="mb-24">
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Nombre"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.name}
                          helperText={errors?.name?.message}
                          required
                          fullWidth
                          onChange={(e) => {
                            const value = e.target.value.toUpperCase();
                            field.onChange(value);
                          }}
                          value={field.value || ''}
                        />
                      )}
                    />
                  </div>

                  {/* Solo mostrar has_description si NO es estado general */}
                  {type !== 'state' && (
                    <div className="mb-32">
                      <FormControlLabel
                        control={
                          <Controller
                            name="has_description"
                            control={control}
                            render={({ field }) => (
                              <Checkbox
                                checked={field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                              />
                            )}
                          />
                        }
                        label="Requiere Observación"
                      />
                    </div>
                  )}

                  {errorCode === ERROR.ERR_409 && (
                    <Typography color="error" className="text-md text-center mb-24">
                      Ya existe un estado con ese nombre
                    </Typography>
                  )}

                  <div className="flex justify-center gap-4">
                    <Button
                      variant="contained"
                      color="error"
                      sx={{ borderRadius: 50 }}
                      disabled={loading}
                      type="button"
                      onClick={onDelete}
                    >
                      <Spinner show={loading} />
                      Eliminar
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{ borderRadius: 50 }}
                      disabled={!isEmpty(errors) || !isValid || loading}
                      type="submit"
                      size="large"
                    >
                      <Spinner show={loading} />
                      Guardar
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateDialog;
